import { toast } from "sonner";
import { ValueEstimationSection } from "../components/ValueEstimationSection";
import { useEffect, useState } from "react";
import { useLead } from "@/hooks/useLead";
import { getLanguage } from "@/utils/languageUtils";
import { leadUILabels } from "@/i18n";
import { mapRatingIntervalsToCategories } from "@/admin/utils/ratingIntervals";
import type { LeadVehicleDetails } from "@/types/lead";
import { isMotorcycle } from "@/utils/vehicleType";

interface LeadValueEstimationProps {
  valueData?: any;
  ratingIntervalsRaw?: any;
  vehicleCardId?: string; // Optional since it might not always be available
  vehicleDetails?: LeadVehicleDetails;
}

export const LeadValueEstimation = ({
  valueData,
  ratingIntervalsRaw,
  vehicleCardId,
  vehicleDetails,
}: LeadValueEstimationProps) => {
  const { fetchRatingIntervalsAsync, isFetchingRatingIntervals } = useLead();
  const [localPriceCategories, setLocalPriceCategories] = useState(
    mapRatingIntervalsToCategories(ratingIntervalsRaw),
  );

  useEffect(() => {
    setLocalPriceCategories(mapRatingIntervalsToCategories(ratingIntervalsRaw));
  }, [ratingIntervalsRaw]);

  const handleRefreshPrices = async () => {
    if (!vehicleCardId) {
      toast.error(leadUILabels(getLanguage()).noLeadIdForRefresh);
      return;
    }
    if (!vehicleDetails) {
      toast.error(leadUILabels(getLanguage()).vehicleDetailsMissing);
      return;
    }
    // AutoUncle does not price motorcycles. Without this the panel walks all six ladder
    // rungs and reports a misleading error; the backend guard is the backstop, but
    // skipping here also avoids a "värdering uppdaterad" toast that would be a lie.
    // Normalized because the lead DTO uses the legacy "motorcycle" spelling.
    if (isMotorcycle(vehicleDetails.vehicleType)) {
      return;
    }

    try {
      // No request body: the backend rebuilds the AutoUncle request from the stored lead row
      // (sync plan 2026-09-08), so this panel cannot drift from the valuation that created it.
      const response = await fetchRatingIntervalsAsync({ leadId: vehicleCardId });
      // Bands update immediately from the response; the headline price re-renders when
      // the invalidated lead query comes back (see useLead).
      setLocalPriceCategories(
        mapRatingIntervalsToCategories(response.ratingIntervals),
      );
      toast.success(leadUILabels(getLanguage()).valueEstimationUpdated);
    } catch (error) {
      toast.error(leadUILabels(getLanguage()).valueEstimationUpdateError);
    }
  };

  // NOTE: this panel deliberately does NOT fetch on open.
  //
  // It used to "self-heal" a lead with no band rows by firing handleRefreshPrices once on
  // first render. That became unsafe when the refresh endpoint started refreshing the
  // headline price as well as the bands (2026-09-03): merely OPENING a lead then re-asked
  // AutoUncle and overwrote estimatedMarketPrice / purchasePrice with today's answer. The
  // stored price is what we quoted the customer, and the market moves, so a read silently
  // destroyed the record of what was promised - the same class of drift this whole
  // migration exists to stop. The backend states the rule for the listing surface
  // (listing.controller.ts: "a page VIEW must never rewrite a card's bands or prices");
  // this panel is now consistent with it, and with the card panel, which never auto-fetched.
  //
  // Old leads with no bands stay empty until an admin presses "Uppdatera priser". That is
  // the intended trade: an empty panel is recoverable with one click, a silently rewritten
  // price is not.

  return (
    <ValueEstimationSection
      valueData={valueData || []}
      priceCategories={localPriceCategories}
      onRefreshPrices={handleRefreshPrices}
      isUpdating={isFetchingRatingIntervals || !vehicleCardId}
      emptyMessage={leadUILabels(getLanguage()).noValueEstimationData}
    />
  );
};
